import dayjs from 'dayjs';
// Types
import { StreamStatus } from 'api/graphql-global-types';
import {
  GetAthleteStreams_liveAndUpcomingStreams_entities,
  GetAthleteStreams_pastStreams_entities,
} from 'api/streams/types/GetAthleteStreams';
// Hooks
import { useGetAthleteStreams } from './useGetAthleteStreams';
import { useRefetchOnUserLogIn } from './useRefetchOnUserLogIn';

type UseAthleteRelatedStreamsProps = {
  storeId: string | undefined;
  currentStreamId?: string;
  totalStreamsToShow?: number;
  upcomingOnly?: boolean;
};

type RelatedStreamsOutput = {
  streams:
    | GetAthleteStreams_liveAndUpcomingStreams_entities[]
    | GetAthleteStreams_pastStreams_entities[];
  total: number;
  loading: boolean;
};

export const useAthleteRelatedStreams = ({
  storeId,
  currentStreamId,
  totalStreamsToShow = 5,
  upcomingOnly = false,
}: UseAthleteRelatedStreamsProps): RelatedStreamsOutput => {
  const { data, loading, refetch } = useGetAthleteStreams({
    storeId: storeId || undefined,
    liveAndUpcomingStreamsInput: {
      scheduleDate: {
        from: dayjs().add(-1, 'day').startOf('day').toDate(), // taking -1 day because of long live streams
        to: dayjs().add(1, 'year').startOf('day').toDate(),
      },
      limit: 5,
      ...(upcomingOnly && {
        streamStatus: [StreamStatus.Scheduled, StreamStatus.Active],
      }),
    },
    pastStreamsInput: {
      limit: 5,
    },
  });

  const liveAndUpcomingStreamList =
    data?.liveAndUpcomingStreams?.entities ?? [];
  const pastStreamList = data?.pastStreams?.entities ?? [];
  const relatedStreams:
    | GetAthleteStreams_liveAndUpcomingStreams_entities[]
    | GetAthleteStreams_pastStreams_entities[] = [];

  liveAndUpcomingStreamList.forEach((stream) => {
    if (
      relatedStreams.length < totalStreamsToShow &&
      stream.id !== currentStreamId
    ) {
      relatedStreams.push(stream);
    }
  });

  if (!upcomingOnly) {
    pastStreamList.forEach((pastStream) => {
      if (
        relatedStreams.length < totalStreamsToShow &&
        pastStream.id !== currentStreamId
      ) {
        relatedStreams.push(pastStream);
      }
    });
  }

  useRefetchOnUserLogIn({ refetch });

  return {
    streams: relatedStreams,
    total: upcomingOnly
      ? data?.liveAndUpcomingStreams?.total ?? 0
      : (data?.liveAndUpcomingStreams?.total ?? 0) +
        (data?.pastStreams?.total ?? 0),
    loading,
  };
};
